import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, LogOut, TrendingUp, Users, DollarSign, Calendar } from 'lucide-react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import LiveBadge from '../components/LiveBadge';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

/**
 * Referral-staff dashboard. Shown to any user whose role is
 * `referral_agent`. They only see the players they brought in and
 * those players' first-deposit numbers. Auto-syncs every 7s so new
 * sign-ups and approved deposits appear without refresh.
 */
export default function ReferralStaffDashboard() {
  const { user, logout } = useAuth();
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ period });
    if (period === 'custom') {
      if (startDate) p.set('start', startDate);
      if (endDate) p.set('end', endDate);
    }
    return p.toString();
  }, [period, startDate, endDate]);

  const fetchDashboard = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/referral-staff/me/dashboard?${queryString}`);
      setData(res.data);
    } catch { if (!silent) toast.error('Failed to load dashboard'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchDashboard(); /* eslint-disable-next-line */ }, [queryString]);
  useAutoRefresh(() => fetchDashboard({ silent: true }), 7000, true);

  const copyCode = () => {
    if (!data?.referral_code) return;
    navigator.clipboard.writeText(data.referral_code);
    toast.success(`Copied ${data.referral_code}`);
  };

  const summary = data?.summary || {};
  const players = data?.players || [];
  const signupLink = data?.referral_code
    ? `${window.location.origin}/auth?ref=${encodeURIComponent(data.referral_code)}`
    : '';

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="ra-dashboard">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#131822] px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-3">Referral Dashboard <LiveBadge /></h1>
          <p className="text-xs text-gray-500">Signed in as {user?.email}</p>
        </div>
        <Button variant="outline" className="border-white/10 text-xs" onClick={logout} data-testid="ra-logout">
          <LogOut className="w-3 h-3 mr-1" /> Sign out
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Referral code + share */}
        <div className="bg-[#131822] rounded-xl border border-[#D4AF37]/30 p-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Your referral code</div>
              <div className="flex items-center gap-3 mt-1">
                <code className="text-3xl font-bold text-[#D4AF37] font-mono" data-testid="ra-code">{data?.referral_code || '—'}</code>
                <Button size="sm" variant="outline" className="border-white/10" onClick={copyCode}><Copy className="w-3 h-3 mr-1" /> Copy</Button>
              </div>
              {signupLink && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Share this link</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={signupLink} className="bg-[#0B0E14] border-white/10 text-xs font-mono" />
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => { navigator.clipboard.writeText(signupLink); toast.success('Link copied'); }}>Copy</Button>
                  </div>
                </div>
              )}
            </div>
            {data && (
              <div className="text-xs text-gray-400">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Your bonus rules</div>
                <div>You earn <span className="text-[#D4AF37] font-bold">{data.bonus_rules?.staff_commission_percent ?? 33}% cash</span> from each referral first deposit</div>
                <div>Your referral gets <span className="text-green-400 font-bold">100% FP</span> as a first-deposit bonus</div>
                {data.bonus_rules?.min_first_deposit_sc != null && <div>Bonus unlocks after <span className="text-white font-bold">${data.bonus_rules.min_first_deposit_sc}</span> first deposit</div>}
              </div>
            )}
          </div>
        </div>

        {/* Period filter */}
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                period === p.key ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`ra-self-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2 ml-3">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-[#131822] border-white/10 text-sm h-8 w-40" />
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-[#131822] border-white/10 text-sm h-8 w-40" />
            </div>
          )}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ra-self-kpis">
          <KPI label="New Players" value={summary.new_players ?? 0} icon={Users} color="#3B82F6" />
          <KPI label="Deposited" value={summary.players_deposited ?? 0} icon={TrendingUp} color="#22c55e" hint={`${summary.conversion_rate ?? 0}% conv.`} />
          <KPI label="1st Deposit Total" value={`$${Math.round(summary.first_deposit_total_sc ?? 0).toLocaleString()}`} icon={DollarSign} color="#D4AF37" />
          <KPI label="Avg 1st Deposit" value={`$${Math.round(summary.avg_first_deposit_sc ?? 0).toLocaleString()}`} color="#8b5cf6" />
          <KPI label="Lifetime Deposits" value={`$${Math.round(summary.total_deposit_sc ?? 0).toLocaleString()}`} color="#f59e0b" hint="by your signups" />
        </div>

        {/* Players */}
        <div className="bg-[#131822] rounded-xl border border-white/10 overflow-x-auto">
          <div className="px-4 py-3 border-b border-white/5">
            <h3 className="text-sm font-semibold">Your Players</h3>
          </div>
          {loading && players.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : players.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No players yet. Share your code to start earning.</div>
          ) : (
            <table className="w-full text-sm" data-testid="ra-self-players">
              <thead><tr className="border-b border-white/5 text-xs text-gray-500">
                <th className="text-left p-3">Player</th>
                <th className="text-left p-3">Signed up</th>
                <th className="text-right p-3">1st Deposit</th>
                <th className="text-left p-3">1st Deposit at</th>
                <th className="text-right p-3">Lifetime Deposits</th>
              </tr></thead>
              <tbody>
                {players.map(p => (
                  <tr key={p.email || p.username} className="border-b border-white/[0.03]">
                    <td className="p-3">
                      <div className="font-medium">{p.username}</div>
                      <div className="text-xs text-gray-500">{p.email}</div>
                    </td>
                    <td className="p-3 text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                    <td className="p-3 text-right text-green-400 font-semibold">{(p.first_deposit_sc ?? 0) > 0 ? `$${Math.round(p.first_deposit_sc).toLocaleString()}` : <span className="text-gray-600">—</span>}</td>
                    <td className="p-3 text-xs text-gray-400">{p.first_deposit_at ? new Date(p.first_deposit_at).toLocaleString() : '—'}</td>
                    <td className="p-3 text-right text-[#D4AF37]">${Math.round(p.total_deposit_sc ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, hint, color = '#D4AF37', icon: Icon = null }) {
  return (
    <div className="bg-[#131822] rounded-xl border p-3" style={{ borderColor: `${color}33` }}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      </div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}
